import React from "react";
import GroupHeader from "./GroupHeader";
import VariantRow from "./VariantRow";
import { REWARD_TYPES, hasVariantsWithQuantity } from "./utils";

const VariantTable = ({
  options,
  setOptions,
  variants,
  setVariants,
  groupBy,
  setGroupBy,
  handleImageUpload,
  handleVariantImageUpload,
  updateVariant,
  handleOptionValueChange,
  opts,
  canAddVariants
}) => {
  const getGroupedVariants = (options, variants) => {
    // Filter out disabled variants
    const enabledVariants = variants.filter(variant => variant.enabled !== false);
    if (options.length <= 1) {
      return { "": enabledVariants };
    }

    const selectedOption = options.find(option => option.title === groupBy);
    if (!selectedOption) return { "": enabledVariants };

    return enabledVariants.reduce((grouped, variant) => {
      const matchingValue = selectedOption.values.find(value => 
        variant.valueIds.includes(value.id)
      );
      const groupName = matchingValue ? matchingValue.name : "Uncategorized";

      if (!grouped[groupName]) {
        grouped[groupName] = [];
      }
      grouped[groupName].push(variant);
      return grouped;
    }, {});
  };

  const renderTableHeader = () => (
    <thead>
      <tr>
        <th className="text-center"></th>
        <th className="text-center"></th>
        {(opts.reward_type === REWARD_TYPES.COMPANY_FULFILLED || 
          opts.reward_type === REWARD_TYPES.AXOMO_REWARD) && (
          <>
            <th className="text-primary">Price</th>
            <th className="text-primary">Quantity</th>
          </>
        )}
        {opts.reward_type === REWARD_TYPES.COMPANY_FULFILLED && (
          <th className="text-primary">SKU</th>
        )}
        {hasVariantsWithQuantity(opts) && <th className="text-primary">Redemptions</th>}
      </tr>
    </thead>
  );

  const handleGroupByChange = (selectedTitle) => {
    setGroupBy(selectedTitle);
    
    // Find the selected option and the first option (lowest order value)
    const selectedOption = options.find(option => option.title === selectedTitle);
    const sortedOptions = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
    const firstOption = sortedOptions[0];
    
    // If the selected option is not already first, swap their positions
    if (selectedOption && firstOption && selectedOption.id !== firstOption.id) {
      const updatedOptions = options.map(opt => {
        if (opt.id === selectedOption.id) {
          return { ...opt, order: firstOption.order || 0 };
        }
        if (opt.id === firstOption.id) {
          return { ...opt, order: selectedOption.order || 0 };
        }
        return opt;
      });
      
      setOptions(updatedOptions.sort((a, b) => (a.order || 0) - (b.order || 0)));
    }
  };

  const renderGroupSelector = () => (
    options.length > 1 && (
      <div className="mt-2 d-flex align-items-center">
        <label className="form-label me-2">Group by</label>
        <select 
          className="form-select w-auto"
          value={groupBy}
          onChange={(e) => handleGroupByChange(e.target.value)}
        >
          {options.map((option) => (
            <option key={option.id} value={option.title}>
              {option.title}
            </option>
          ))}
        </select>
      </div>
    )
  );

  const renderDiscreteTypeVariant = () => {
    if (!variants || variants.length === 0) return null;

    return (
      <div className="p-2 variants-container">
        <div className="error-container" id="reward_variants"></div>
        <label className="form-label fw-bold">Variants</label>
        {variants.map((variant) => {
          const handleCheckboxChange = (e) => {
            const isChecked = e.target.checked;            
            setVariants(prevVariants => 
              prevVariants.map(v => 
                v.id === variant.id 
                  ? { ...v, enabled: isChecked }
                  : v
              )
            );
          };

          const checkboxId = `variant-checkbox-${variant.id}`;

          return (
            <div key={variant.id} className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id={checkboxId}
                checked={variant.enabled || false}
                onChange={handleCheckboxChange}
              />
              <label className="form-check-label" htmlFor={checkboxId}>
                {variant.label}
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  // Don't show full variants container for variable type and discrete type rewards
  if (opts.reward_type === REWARD_TYPES.VARIABLE_TYPE) {
    return null;
  }

  if (opts.reward_type === REWARD_TYPES.DISCRETE_TYPE) {
    return renderDiscreteTypeVariant();
  }

  return (
    <div className="p-2 variants-container">
      <label className="form-label fw-bold">Variants</label>
      {renderGroupSelector()}
      <div className="error-container" id="reward_variants"></div>

      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <table className="table">
          {renderTableHeader()}
          <tbody>
            {Object.entries(getGroupedVariants(options, variants)).map(([groupName, groupVariants]) => (
              <React.Fragment key={groupName}>
                {options.length > 1 && groupBy && (
                  <GroupHeader 
                    name={groupName} 
                    count={groupVariants.length}
                    groupedVariants={groupVariants}
                    options={options}
                    setOptions={setOptions}
                    setVariants={setVariants}
                    handleImageUpload={handleImageUpload}
                    handleOptionValueChange={handleOptionValueChange}
                    updateVariant={updateVariant}
                    opts={opts}
                    canAddVariants={canAddVariants}
                  />
                )}
                {groupVariants.map((variant, index) => (
                  <VariantRow
                    key={variant.id}
                    variant={variant}
                    index={index}
                    setVariants={setVariants}
                    handleImageUpload={handleImageUpload}
                    handleVariantImageUpload={handleVariantImageUpload}
                    updateVariant={updateVariant}
                    opts={opts}
                    canAddVariants={canAddVariants}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VariantTable;